// EN mirror of /checklist — visa document checklists
import { createFileRoute, Link } from "@tanstack/react-router";
import { ListChecks, FileCheck2 } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { howToSchema } from "@/lib/aeo";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "Visa Document Checklists — Thailand Inbound & Outbound | NYC Language Institute";
const DESC =
  "Document checklists for Thai Non-B, LTR, DTV, marriage, retirement. Free consultation — message us and get a same-day answer.";

const CHECKLISTS = [
  { slug: "non-b", title: "Non-B Work Visa", items: 11 },
  { slug: "ltr", title: "LTR (Long-Term Resident)", items: 14 },
  { slug: "dtv", title: "DTV (Destination Thailand Visa)", items: 9 },
  { slug: "retirement-o", title: "Retirement O / O-A / O-X", items: 10 },
  { slug: "marriage", title: "Marriage Visa (O — Spouse)", items: 12 },
  { slug: "ed", title: "ED (Education) Visa", items: 8 },
  { slug: "smart", title: "Smart Visa", items: 10 },
  { slug: "elite", title: "Thailand Elite / Privilege", items: 6 },
  { slug: "schengen-tourist", title: "Outbound Schengen (Tourist)", items: 11 },
  { slug: "us-b1-b2", title: "Outbound US B1/B2", items: 12 },
  { slug: "uk-standard-visitor", title: "Outbound UK Standard Visitor", items: 10 },
  { slug: "au-subclass-600", title: "Outbound Australia Subclass 600", items: 11 },
];

export const Route = createFileRoute("/en/checklist")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/checklist", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/checklist", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/checklist", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/checklist", "en")},
      { property: "og:url", content: `${SITE}/en/checklist` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/en/checklist` },
      ...hreflangLinks("/checklist", "/en/checklist"),
    ],
    scripts: [
      howToSchema({
        name: "How to use the visa document checklist",
        description: "Step-by-step guide to using NYC's visa checklists to prepare a complete, embassy-ready document set on the first try.",
        inLanguage: "en", totalTime: "PT7D", estimatedCost: { currency: "THB", value: "250" },
        steps: [
          { name: "Pick your visa type", text: "Click your target visa (Non-B, LTR, DTV, Marriage, Retirement, ED, Smart, Elite, Schengen, US, UK, AU)." },
          { name: "Download checklist + tick documents you have", text: "Compare against the originals at home — anything missing, request from your district / amphoe office." },
          { name: "Send to NYC for translation + certification", text: "Forward checklist + scans via LINE — quote within 30 minutes.", url: `${SITE}/en/translate` },
          { name: "Add Apostille / Legalization (if required)", text: "Most outbound visas require MFA + Apostille — we handle the entire chain.", url: `${SITE}/en/apostille` },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: TITLE,
          url: `${SITE}/en/checklist`,
          inLanguage: "en",
        }),
      }],
  }),
  component: EnChecklistHub,
});

function EnChecklistHub() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-16 text-white">
        <div className="mx-auto max-w-5xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Visa document <span className="text-amber-300">checklists</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/80">
            Each checklist maps every required document to the translation and legalization steps we provide —
            so you arrive at the embassy or immigration counter with nothing missing.
          </p>
          <div className="quick-answer mt-6 max-w-2xl rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-300">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">What documents do I need for a Thai visa?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">Thai visa documents vary by type — Non-B requires 11 items (passport, work permit application, bank statement, company docs), LTR needs 14, DTV needs 9. NYC provides verified 2026 checklists for 12 visa types plus outbound Schengen, US, UK, and AU. Each item links to translation and apostille services where required.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="px-4 py-14">
        <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CHECKLISTS.map((c) => (
            <article key={c.slug} className="rounded-2xl border border-slate-200 bg-white p-5">
              <ListChecks className="h-6 w-6 text-amber-600" />
              <h2 className="mt-3 text-base font-bold text-navy-deep">{c.title}</h2>
              <p className="mt-1 text-xs text-slate-500">{c.items} required items</p>
              <Link to="/checklist" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-amber-700">
                <FileCheck2 className="h-3.5 w-3.5" /> View Thai checklist
              </Link>
            </article>
          ))}
        <RelatedHubGrid locale="en" />
      </div>
      </section>
    </main>
  );
}
